/* eslint-disable no-unused-vars */
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Button } from 'antd';
import FeatherIcon from 'feather-icons-react';
import AdvancedTable from '../../../components/AdvancedTable';
import { GetAppoinmentApi } from '../../../utils/api';
import { matchPermissions } from '../../../utils/Permission';
import Header from '../../../components/Header';
import Sidebar from '../../../components/Sidebar';
import NotesModal from './NotesModal';
import { getColumns } from './columns';
import { filterConfig, summaryConfig, defaultSort } from './config';
import dayjs from 'dayjs';
import { PERMISSIONS } from '../../../constants';


const AppoinmentListBooking = () => {
  const { userDetails } = useSelector(state => state?.auth);
  const location = useLocation();
  const params = queryString.parse(location.search);

  // Notes modal state
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Permissions
  const matchPermissionsForUser = matchPermissions(userDetails);
  const canView = matchPermissionsForUser(PERMISSIONS.APPOINTMENTS.VIEW);
  const canCreate = matchPermissionsForUser(PERMISSIONS.APPOINTMENTS.CREATE);
  const canUpdate = matchPermissionsForUser(PERMISSIONS.APPOINTMENTS.UPDATE);
  const canExport = matchPermissionsForUser(PERMISSIONS.APPOINTMENTS.EXPORT);

  // Handle notes click
  const handleNotesClick = useCallback(
    row => {
      setSelectedAppointment(row);
      setNotesModalVisible(true);
    },
    []
  );

  // Handle notes modal close
  const handleNotesModalClose = useCallback(() => {
    setNotesModalVisible(false);
    setSelectedAppointment(null);
  }, []);

  // Handle notes update success
  const handleNotesUpdate = useCallback(
    (updatedNotes) => {
      handleNotesModalClose();
      // Refresh the table to show updated notes
      setRefreshCounter(prev => prev + 1);
    },
    [handleNotesModalClose]
  );

  // Memoize columns
  const columns = useMemo(
    () => getColumns(params, canUpdate, handleNotesClick),
    [params?.page, params?.perPage, canUpdate, handleNotesClick, refreshCounter]
  );

  // Fetch data function
  const fetchAppointments = useCallback(async ({
    page,
    perPage,
    sortField,
    sortOrder,
    search,
    ...filters
  }) => {
    try {
      const queryParams = {
        page,
        limit: perPage,
        ...(sortField && { sortField }),
        ...(sortOrder && { sortOrder }),
        ...(search && { search }),
        ...filters,
        // Map date range filters if they exist in filters
        ...(filters.dateRange_start && { orderStartDate: dayjs(filters.dateRange_start).format('YYYY-MM-DD') }),
        ...(filters.dateRange_end && { orderEndDate: dayjs(filters.dateRange_end).format('YYYY-MM-DD') }),
      };

      // Remove original dateRange keys if mapped
      if (queryParams.dateRange_start) delete queryParams.dateRange_start;
      if (queryParams.dateRange_end) delete queryParams.dateRange_end;

      const queryStr = queryString.stringify(queryParams);
      const response = await GetAppoinmentApi({ Query: `?${queryStr}` });

      return {
        data: response?.data?.data?.docs || response?.data?.docs || [],
        total: response?.data?.data?.totalDocs || response?.data?.totalDocs || 0,
      };
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return { data: [], total: 0 };
    }
  }, []);

  // Memoize custom toolbar actions
  const customActions = useMemo(
    () =>
      canCreate ? (
        <Button
          type="primary"
          icon={<FeatherIcon icon="plus" size={14} />}
          onClick={() => (window.location.href = '/add-appoinment?step=1')}
        >
          Add Appointment
        </Button>
      ) : null,
    [canCreate]
  );

  // Memoize row click handler
  const handleRowClick = useCallback(
    row => {
      if (canUpdate && row.status !== 'Completed' && row.status !== 'Cancelled') {
        window.location.href = `/patient-list/appointment-details?page=${params?.page || 1
          }&appointmentId=${row?._id}&userId=${row?.customerId?._id}&consultingFor=${row?.consultingFor
          }`;
      }
    },
    [canUpdate, params?.page]
  );

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumbs */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={`/appoinment-list?page=${params?.page || 1}`}>Appointment</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Appointment List</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Advanced Table */}
          {canView ? (
            <AdvancedTable
              key={`appointments-table-${refreshCounter}`}
              resource="appointments"
              columns={columns}
              fetchData={fetchAppointments}
              enableSearch
              enableFilters
              enableExport={canExport}
              enableSummary
              enableColumnManager
              filterConfig={filterConfig}
              summaryConfig={summaryConfig}
              defaultPageSize={10}
              defaultSort={defaultSort}
              defaultFilters={{ status: 'Upcoming' }}
              density="comfortable"
              stickyHeader
              striped
              enableHorizontalScrollFeature={true}
              enableHorizontalScroll={false}
              highlightOnHover
              tableId="appointments-table"
              persistState
              exportFileName="appointments_export"
              onRowClick={handleRowClick}
              customActions={customActions}
            />
          ) : (
            <div className="permission-denied-message">
              You do not have permission to view the appointment list.
            </div>
          )}
        </div>
      </div>

      {/* Notes Modal */}
      <NotesModal
        visible={notesModalVisible}
        onCancel={handleNotesModalClose}
        appointmentId={selectedAppointment?._id}
        currentNotes={selectedAppointment?.notes}
        onSuccess={handleNotesUpdate}
      />
    </>
  );
};

export default AppoinmentListBooking;
